import { apiPost } from "./api";

// Client-side analytics queue. Page-view / action beacons are collected in
// memory and flushed together so the app makes far fewer network calls:
//   • a partial batch is sent after a short idle delay, or
//   • immediately once enough events pile up, or
//   • when the tab is hidden / unloaded (via navigator.sendBeacon).
// Every failure is swallowed — tracking must never affect the experience.

const BASE = process.env.REACT_APP_API_BASE || "/api";
const IDLE_FLUSH_MS = 10000; // flush a partial batch after this much quiet time
const MAX_BATCH = 15; // flush immediately once this many events queue up

let queue = [];
let timer = null;
let currentToken = null;
let lastPath = "";

function clearTimer() {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}

function send(events, useBeacon) {
  const payload = { events, token: currentToken || undefined };
  // On unload the only reliable transport is sendBeacon (it can't set headers,
  // so the token rides in the body and the API attributes it from there).
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(`${BASE}/track`, blob);
      return;
    } catch (e) {
      /* fall through to a normal request */
    }
  }
  apiPost("/track", payload, currentToken).catch(() => {});
}

function flush(useBeacon) {
  clearTimer();
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  send(events, useBeacon);
}

function enqueue(ev) {
  queue.push(ev);
  if (queue.length >= MAX_BATCH) {
    flush(false);
  } else {
    clearTimer();
    timer = setTimeout(() => flush(false), IDLE_FLUSH_MS);
  }
}

// Keep the tracker in sync with the signed-in account so batches are attributed.
export function setTrackToken(token) {
  currentToken = token || null;
}

export function trackPageview(path, ref) {
  const p = path || "/";
  if (p === lastPath) return; // skip immediate repeats of the same route
  lastPath = p;
  enqueue({ type: "pageview", path: p, ref: ref || "" });
}

export function trackAction(name, path) {
  enqueue({ type: "action", name: name || "", path: path || "" });
}

// Make sure a session's queued events aren't lost when the user leaves.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => flush(true));
}
